<div class="dashboard-shell">
  <div class="hero">
    <div class="hero-main">
      <div>
        <p class="eyebrow">Executive Dashboard</p>
        <h2>Operations Command Center</h2>
        <p>Live view of deliveries, fleet, riders, maintenance, supply, staff, and project delivery.</p>
      </div>
      <div class="hero-actions">
        <button mat-flat-button color="primary" (click)="loadDashboard()">
          <mat-icon>refresh</mat-icon>
          Refresh
        </button>
        <button mat-stroked-button color="accent" (click)="maybeShowTutorial()">
          <mat-icon>help</mat-icon>
          Tutorial
        </button>
      </div>
    </div>
    <div class="hero-brief" *ngIf="data" [ngClass]="commandTone">
      <span class="status-chip">{{ commandStatus }}</span>
      <h3>{{ userName }}</h3>
      <p>Use this board for executive scanning, then jump directly into the module that needs intervention.</p>
      <div class="brief-grid">
        <div class="brief-item" *ngFor="let item of attentionItems" [ngClass]="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>
      </div>
    </div>
  </div>

  <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

  <div class="empty-state" *ngIf="!loading && !data">
    Dashboard data is unavailable right now. Click refresh.
  </div>

  <ng-container *ngIf="data">
    <section class="workspace-strip" *ngIf="moduleLinks.length">
      <article class="workspace-card" *ngFor="let item of moduleLinks" [ngClass]="item.tone" [routerLink]="item.route">
        <div>
          <span class="workspace-kicker">Workspace</span>
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
        </div>
        <div class="workspace-meta">
          <strong>{{ item.metric }}</strong>
          <span>Open {{ item.title }}</span>
        </div>
      </article>
    </section>

    <div class="kpi-grid">
      <div class="kpi-card">
        <div class="label">Today's Revenue</div>
        <div class="value">₦{{ data.revenue.today | number }}</div>
        <div class="meta">Pending ₦{{ data.revenue.pending | number }}</div>
      </div>
      <div class="kpi-card">
        <div class="label">Delivery Completion</div>
        <div class="value">{{ percentPaid }}%</div>
        <div class="meta">{{ data.delivery.paid }} paid of {{ data.delivery.total }}</div>
      </div>
      <div class="kpi-card">
        <div class="label">Fleet Compliance</div>
        <div class="value">{{ meterClosePercent }}%</div>
        <div class="meta">{{ data.vehicles.meterClosedToday }}/{{ data.vehicles.meterOpenedToday }} closed logs</div>
      </div>
      <div class="kpi-card">
        <div class="label">Fleet Distance (30d)</div>
        <div class="value">{{ data.vehicles.totalDistance30 | number:'1.0-0' }} km</div>
        <div class="meta">Avg {{ data.vehicles.averageDistance30 | number:'1.0-1' }} km/log</div>
      </div>
      <div class="kpi-card">
        <div class="label">Phone Compliance</div>
        <div class="value">{{ phoneClosePercent }}%</div>
        <div class="meta">{{ data.riders.phoneClosedToday }}/{{ data.riders.phoneOpenedToday }} closed logs</div>
      </div>
      <div class="kpi-card">
        <div class="label">Supply Net Value</div>
        <div class="value">₦{{ data.supply.netAmount | number }}</div>
        <div class="meta">{{ data.supply.todayDispatches }} dispatches today</div>
      </div>
      <div class="kpi-card">
        <div class="label">Staff Requests</div>
        <div class="value">{{ data.staff.totalRequests }}</div>
        <div class="meta">{{ data.staff.pendingRequests }} pending approvals</div>
      </div>
      <div class="kpi-card">
        <div class="label">Project Portfolio</div>
        <div class="value">{{ data.projects.totalProjects }}</div>
        <div class="meta">{{ data.projects.activeProjects }} active projects</div>
      </div>
    </div>

    <div class="analytics-grid">
      <section class="panel">
        <h3>Delivery Distribution</h3>
        <div class="stack-bar">
          <div
            *ngFor="let seg of deliveryDistribution"
            class="segment"
            [style.width.%]="seg.pct"
            [style.background]="seg.color">
          </div>
        </div>
        <div class="legend">
          <div *ngFor="let seg of deliveryDistribution" class="legend-item">
            <span class="dot" [style.background]="seg.color"></span>
            <span>{{ seg.label }}</span>
            <strong>{{ seg.value }} ({{ seg.pct }}%)</strong>
          </div>
        </div>
      </section>

      <section class="panel">
        <h3>Revenue Trend</h3>
        <div class="bar-chart">
          <div *ngFor="let item of revenueSeries" class="bar-col">
            <div class="bar" [style.height.%]="revenueBarHeight(item.value)"></div>
            <span class="amt">₦{{ item.value | number:'1.0-0' }}</span>
            <span class="lbl">{{ item.label }}</span>
          </div>
        </div>
        <div class="foot-note">
          Gross margin this month: <strong>₦{{ grossMarginThisMonth | number }}</strong>
        </div>
      </section>

      <section class="panel">
        <h3>Utilization Rings</h3>
        <div class="rings">
          <div class="ring-wrap">
            <div class="ring" [style.background]="'conic-gradient(#2563eb ' + vehicleAssignPercent + '%, #e5e7eb 0)'">
              <span>{{ vehicleAssignPercent }}%</span>
            </div>
            <p>Vehicle Assigned</p>
          </div>
          <div class="ring-wrap">
            <div class="ring" [style.background]="'conic-gradient(#ea580c ' + riderActivePercent + '%, #e5e7eb 0)'">
              <span>{{ riderActivePercent }}%</span>
            </div>
            <p>Rider Active</p>
          </div>
        </div>
        <div class="mini-stats">
          <div>Avg Start Fuel: <strong>{{ data.vehicles.averageStartFuelLevel | number:'1.0-2' }}%</strong></div>
          <div>Avg End Fuel: <strong>{{ data.vehicles.averageEndFuelLevel | number:'1.0-2' }}%</strong></div>
          <div>Avg Fuel Used: <strong>{{ data.vehicles.averageFuelUsed | number:'1.0-2' }}%</strong></div>
          <div>Total Distance Today: <strong>{{ data.vehicles.totalDistanceToday | number:'1.0-2' }}</strong></div>
        </div>
      </section>

      <section class="panel">
        <h3>Maintenance Health</h3>
        <div class="metrics-grid">
          <div>
            <span>Flagged Vehicles</span>
            <strong>{{ data.maintenance.totalFlagged }}</strong>
          </div>
          <div>
            <span>Due This Week</span>
            <strong>{{ data.maintenance.dueThisWeek }}</strong>
          </div>
          <div>
            <span>This Month Cost</span>
            <strong>₦{{ data.maintenance.thisMonthCost | number }}</strong>
          </div>
          <div>
            <span>Total Cost</span>
            <strong>₦{{ data.maintenance.totalCost | number }}</strong>
          </div>
        </div>
        <div class="mix-bars">
          <div class="mix-row">
            <label>Service</label>
            <div class="mix-track"><div class="mix-fill svc" [style.width.%]="(maintenanceMix.services / maintenanceMix.total) * 100"></div></div>
            <strong>{{ data.maintenance.serviceCount }}</strong>
          </div>
          <div class="mix-row">
            <label>Repair</label>
            <div class="mix-track"><div class="mix-fill rep" [style.width.%]="(maintenanceMix.repairs / maintenanceMix.total) * 100"></div></div>
            <strong>{{ data.maintenance.repairCount }}</strong>
          </div>
        </div>
      </section>

      <section class="panel">
        <h3>Supply Snapshot</h3>
        <div class="metrics-grid">
          <div>
            <span>Total Dispatches</span>
            <strong>{{ data.supply.totalDispatches }}</strong>
          </div>
          <div>
            <span>Total Units</span>
            <strong>{{ data.supply.totalUnits | number:'1.0-2' }}</strong>
          </div>
          <div>
            <span>Gross Amount</span>
            <strong>₦{{ data.supply.grossAmount | number }}</strong>
          </div>
          <div>
            <span>Net Amount</span>
            <strong>₦{{ data.supply.netAmount | number }}</strong>
          </div>
          <div>
            <span>Commission Rate</span>
            <strong>{{ supplyCommissionRate }}%</strong>
          </div>
          <div>
            <span>Month Gross</span>
            <strong>₦{{ data.supply.thisMonthGrossAmount | number }}</strong>
          </div>
        </div>
      </section>

      <section class="panel">
        <h3>Staff & Request Snapshot</h3>
        <div class="metrics-grid">
          <div>
            <span>Total Staff</span>
            <strong>{{ data.staff.totalStaff }}</strong>
          </div>
          <div>
            <span>Active Staff</span>
            <strong>{{ data.staff.activeStaff }}</strong>
          </div>
          <div>
            <span>Approval Rate</span>
            <strong>{{ staffRequestApprovalRate }}%</strong>
          </div>
          <div>
            <span>Pending Requests</span>
            <strong>{{ data.staff.pendingRequests }}</strong>
          </div>
          <div>
            <span>Funds Requested</span>
            <strong>₦{{ data.staff.fundsRequestedAmount | number }}</strong>
          </div>
          <div>
            <span>Loan Requested</span>
            <strong>₦{{ data.staff.loanRequestedAmount | number }}</strong>
          </div>
          <div>
            <span>Leave Requests</span>
            <strong>{{ data.staff.leaveRequests }}</strong>
          </div>
          <div>
            <span>Rejected Requests</span>
            <strong>{{ data.staff.rejectedRequests }}</strong>
          </div>
        </div>
      </section>

      <section class="panel">
        <h3>Project Snapshot</h3>
        <div class="metrics-grid">
          <div>
            <span>Total Projects</span>
            <strong>{{ data.projects.totalProjects }}</strong>
          </div>
          <div>
            <span>Active Projects</span>
            <strong>{{ data.projects.activeProjects }}</strong>
          </div>
          <div>
            <span>Completed Projects</span>
            <strong>{{ data.projects.completedProjects }}</strong>
          </div>
          <div>
            <span>Total Tasks</span>
            <strong>{{ data.projects.totalTasks }}</strong>
          </div>
          <div>
            <span>Completed Tasks</span>
            <strong>{{ data.projects.completedTasks }}</strong>
          </div>
          <div>
            <span>Pending Tasks</span>
            <strong>{{ data.projects.pendingTasks }}</strong>
          </div>
          <div>
            <span>Task Completion</span>
            <strong>{{ projectTaskCompletionRate }}%</strong>
          </div>
          <div>
            <span>Avg Progress</span>
            <strong>{{ data.projects.averageProjectProgress | number:'1.0-0' }}%</strong>
          </div>
          <div>
            <span>Onboarding Items</span>
            <strong>{{ data.projects.totalOnboardingItems }}</strong>
          </div>
        </div>
      </section>

      <section class="panel alerts">
        <h3>Recent Alerts</h3>
        <div class="alert-list">
          <article *ngFor="let n of data.notifications.recent" class="alert-item">
            <div class="title">{{ n.title }}</div>
            <div class="msg">{{ n.message }}</div>
            <div class="time">{{ n.createdAt | date:'medium' }}</div>
          </article>
          <div class="empty" *ngIf="!data.notifications.recent?.length">No alerts.</div>
        </div>
        <div class="unread">Unread: <strong>{{ data.notifications.unreadCount }}</strong></div>
      </section>
    </div>
  </ng-container>

  <div class="updated">Last updated: {{ lastUpdated | date:'medium' }}</div>
</div>
